/**
 * ChatMessage - Individual message bubble
 */

import { format } from 'date-fns'
import { FileText, Download } from 'lucide-react'
import AttachmentPreview from './AttachmentPreview'
import { getFirstNameOnly } from '../../lib/privacy/sanitizeTalentData'

interface Message {
  id: string
  sender_id: string
  message_text: string
  attachment_url?: string
  attachment_type?: string
  attachment_name?: string
  created_at: string
  sender: {
    id: string
    full_name: string
    avatar_url?: string
  }
}

interface ChatMessageProps {
  message: Message
  isOwnMessage: boolean
}

export default function ChatMessage({
  message,
  isOwnMessage
}: ChatMessageProps) {
  const messageTime = format(new Date(message.created_at), 'h:mm a')
  const hasAttachment = !!message.attachment_url

  const displayName = getFirstNameOnly(message.sender?.full_name || 'Love & Photos')

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
        {/* Sender name (only for received messages) */}
        {!isOwnMessage && (
          <div className="text-xs text-gray-500 mb-1 px-3">
            {displayName}
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`rounded-lg px-4 py-2 ${
            isOwnMessage
              ? 'bg-primary text-white'
              : 'bg-white border border-gray-200 text-gray-900'
          }`}
        >
          {/* Attachment */}
          {hasAttachment && (
            <div className="mb-2">
              <AttachmentPreview
                url={message.attachment_url!}
                type={message.attachment_type!}
                name={message.attachment_name!}
                isOwnMessage={isOwnMessage}
              />
            </div>
          )}

          {/* Message text */}
          <div className="whitespace-pre-wrap break-words">
            {message.message_text}
          </div>

          {/* Timestamp */}
          <div
            className={`text-xs mt-1 ${
              isOwnMessage ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            {messageTime}
          </div>
        </div>
      </div>
    </div>
  )
}
